/**
 * FK: オーダー商品（シミュレーター）のオプション加算 — 決済前の照合（2026-10-27）
 *
 * シミュレーターから入るオーダーは「本体 1 行 ＋ オプション加算 N 行」で、どの行も同じ `_仕様`（と `_編集`）を持つ。
 * 数量は変えられない（snippets/cart-products.liquid）が、行の削除はできる ⇒ 片方だけ消すと数が合わなくなる。
 * カートで「ご購入手続きへ」を押したとき、オーダーごとに本体が持つ `_加算`（入れたはずの加算）と、実際の加算の行を比べ、
 * 合わないときだけ確認を出す。ボタンは〔合わせて進む〕〔カートに戻って直す〕。〔このまま進む〕は〔合わせて進む〕が失敗したときだけ。
 * 🔴 お客様を止めない＝照合そのものが失敗したら何も出さずに進める。自動では直さない（直すのはお客様が押したときだけ）。
 * 管理画面「FK オーダー商品」で OFF にすると何もしない。
 *
 * 照合の決まり:
 * - 本体が無い加算の行（本体を消した）＝ 余分 ⇒ 0 にする。
 * - 本体の `_加算` より少ない・多い加算 ⇒ `_加算` の数に合わせる。
 * - ⚠️ `_加算` を持たない本体（2026-10-27 より前に入った行）は、足りない方は判定しない（余分な加算だけ見る）。
 *
 * 📌 カートへ入れる処理（本体に `_加算` を付ける）は templates/cart.fk-sim.liquid が持つ。
 * 📌 プリント加工費の照合（fk-print-option.js）と同じ形。2 つとも走るときは、こちらが先に走る（layout/theme.liquid の読み込み順）。
 */

const SPEC_KEY = '_仕様';
const EDIT_KEY = '_編集';
const EXPECT_KEY = '_加算';

/** 加算の行＝隠し項目の `_仕様`・`_編集` しか持たない行（本体は選択内容や `_加算` を持つ） */
const isAddonLine = (item) => {
  const keys = Object.keys(item.properties || {});
  return keys.includes(SPEC_KEY) && keys.every((k) => k === SPEC_KEY || k === EDIT_KEY);
};

/** 足りない加算の名前（`_加算` に書いてある名前。カートに行が無いので、ここからしか引けない） */
const NAMES = new Map();

/** `_加算`（"変種ID:数:名前,…"・加算なしは "なし"）→ Map（変種ID → 数）。項目が無ければ null（旧い行） */
function parseExpected(value) {
  if (value === undefined || value === null) return null;
  const map = new Map();
  for (const part of String(value).split(',')) {
    const [id, qty, ...name] = part.split(':');
    if (!/^\d+$/.test(id || '') || !/^\d+$/.test(qty || '')) continue;
    map.set(Number(id), (map.get(Number(id)) || 0) + Number(qty));
    if (name.length) NAMES.set(Number(id), name.join(':'));
  }
  return map;
}

async function fetchCart() {
  const res = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`cart.js ${res.status}`);
  return res.json();
}

async function postJson(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.description || json.message || `${url} ${res.status}`);
  return json;
}

/**
 * 照合の結果＝数が合わないオーダーだけ（合っていれば空）。
 * 1 件＝{ key, spec, edit, body, lines: [{ variantId, title, have, want, items }] }
 */
function findMismatches(items) {
  const orders = new Map();
  const orderOf = (item) => {
    const p = item.properties || {};
    const key = p[EDIT_KEY] || p[SPEC_KEY];
    if (!orders.has(key)) orders.set(key, { key, spec: p[SPEC_KEY], edit: p[EDIT_KEY], bodies: [], addons: [] });
    return orders.get(key);
  };
  for (const item of items) {
    if (!item.properties?.[SPEC_KEY]) continue;
    const order = orderOf(item);
    (isAddonLine(item) ? order.addons : order.bodies).push(item);
  }

  const result = [];
  for (const order of orders.values()) {
    // 入れたはずの加算（本体の数ぶん）。本体が無ければ全部余分。`_加算` が無い旧い本体は足りない方を見ない
    const bodyQty = order.bodies.reduce((n, b) => n + b.quantity, 0);
    const expectedPer = order.bodies.length ? parseExpected(order.bodies[0].properties[EXPECT_KEY]) : new Map();
    const strict = expectedPer !== null;
    const want = new Map();
    if (strict) for (const [id, qty] of expectedPer) want.set(id, qty * bodyQty);

    const have = new Map();
    for (const a of order.addons) {
      const id = Number(a.variant_id);
      if (!have.has(id)) have.set(id, { qty: 0, items: [], title: a.variant_title || a.product_title });
      const h = have.get(id);
      h.qty += a.quantity;
      h.items.push(a);
    }

    const lines = [];
    for (const id of new Set([...want.keys(), ...have.keys()])) {
      const h = have.get(id) || { qty: 0, items: [], title: '' };
      const w = strict ? want.get(id) || 0 : order.bodies.length ? h.qty : 0;
      if (h.qty !== w) lines.push({ variantId: id, title: h.title, have: h.qty, want: w, items: h.items });
    }
    if (lines.length) result.push({ key: order.key, spec: order.spec, edit: order.edit, body: order.bodies[0], lines });
  }
  return result;
}

const escapeHtml = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const lineName = (line) => line.title || NAMES.get(line.variantId) || 'オプション';

function describeSummary(mismatches) {
  let short = 0;
  let over = 0;
  for (const m of mismatches) for (const l of m.lines) {
    if (l.have < l.want) short += l.want - l.have;
    else over += l.have - l.want;
  }
  const blocks = [];
  if (short) blocks.push([`⚠️ 足りないオプションが ${short} つあります`, '※このままでは、選んだ内容のとおりに作れません']);
  if (over) blocks.push([`⚠️ 余分なオプションが ${over} つあります`, `※このままでは ${over} つ分多く支払ってしまいます`]);
  return blocks
    .map(([head, note]) => `<p class="fk-sim-check__verdict">${head}</p><p class="fk-sim-check__note">${note}</p>`)
    .join('');
}

function describeOrder(m) {
  const head = m.body
    ? `${escapeHtml(m.body.product_title)}${m.body.variant_title ? `（${escapeHtml(m.body.variant_title)}）` : ''}`
    : '（オーダー商品の本体がカートにありません）';
  const rows = m.lines
    .map((l) => {
      const diff = l.have - l.want;
      const tag = diff > 0 ? `${diff} つ多い` : `${-diff} つ不足`;
      return `<p class="fk-sim-check__count"><span>${escapeHtml(lineName(l))}：<span class="fk-sim-check__bad">${l.have}</span> つ（正しくは ${l.want} つ）</span><span class="fk-sim-check__tag">${tag}</span></p>`;
    })
    .join('');
  return `<li class="fk-sim-check__item"><p class="fk-sim-check__spec">${head}</p>${rows}</li>`;
}

/** 数を合わせる＝多い分は減らし、足りない分はそのオーダーの印（`_仕様`・`_編集`）を付けて足す */
async function fixMismatches(mismatches) {
  const updates = {};
  const adds = [];
  for (const m of mismatches) for (const l of m.lines) {
    if (l.have > l.want) {
      let remaining = l.want;
      for (const item of l.items) {
        const keep = Math.min(item.quantity, remaining);
        updates[item.key] = keep;
        remaining -= keep;
      }
    } else {
      const properties = { [SPEC_KEY]: m.spec };
      if (m.edit) properties[EDIT_KEY] = m.edit;
      adds.push({ id: l.variantId, quantity: l.want - l.have, properties });
    }
  }
  if (Object.keys(updates).length) await postJson('/cart/update.js', { updates });
  if (adds.length) await postJson('/cart/add.js', { items: adds });
}

/**
 * 直したあとの行き先。
 * 🔴 カートのフォームは数量（updates[]）も送るので、送り直すと直した数が戻る ⇒ attributes とメモだけ保存して決済画面へ。
 * ⚠️ プリントのある商品も入っているときは、カートを開き直す（プリント加工費の照合をもう一度通すため）。
 */
async function goOnKeepingForm(form, items) {
  const attributes = {};
  let note;
  for (const [name, value] of new FormData(form).entries()) {
    const m = /^attributes\[(.+)\]$/.exec(name);
    if (m) attributes[m[1]] = String(value);
    else if (name === 'note') note = String(value);
  }
  const body = { attributes };
  if (note !== undefined) body.note = note;
  const cart = await postJson('/cart/update.js', body);
  // 本体を消して加算だけが残っていた ⇒ 合わせると空になる。空のまま決済へ送らない
  if (!cart.item_count) {
    window.location.href = window.Theme?.routes?.cart_url || '/cart';
    return;
  }
  const hasPrint = items.some((i) => i.properties?.['プリント内容'] || i.properties?.['ご注意']);
  window.location.href = hasPrint && window.FK_PRINT_CHECK_ENABLED ? (window.Theme?.routes?.cart_url || '/cart') : '/checkout';
}

function openDialog(mismatches, { onFix, onContinue }) {
  document.getElementById('fk-sim-check')?.remove();
  const dialog = document.createElement('dialog');
  dialog.id = 'fk-sim-check';
  dialog.className = 'fk-sim-check';
  dialog.innerHTML = `
    <style>
      .fk-sim-check { max-width: 30rem; width: calc(100% - 2rem); padding: 1.25rem; border: none; border-radius: 8px; box-shadow: 0 8px 32px rgb(0 0 0 / 0.25); }
      .fk-sim-check::backdrop { background: rgb(0 0 0 / 0.4); }
      .fk-sim-check h2 { font-size: 1.1em; margin: 0 0 0.75rem; }
      .fk-sim-check__verdict { color: #b71c1c; font-weight: bold; margin: 0; }
      .fk-sim-check__note { color: #b71c1c; font-size: 0.9em; margin: 0.15rem 0 0.75rem; }
      .fk-sim-check__label { font-size: 0.85em; margin: 0.75rem 0 0.25rem; opacity: 0.7; }
      .fk-sim-check ul { list-style: none; margin: 0 0 1rem; padding: 0 0.75rem; border: 1px solid #e0e0e0; border-radius: 6px; font-size: 0.9em; }
      .fk-sim-check__item { padding: 0.75rem 0; }
      .fk-sim-check__item + .fk-sim-check__item { border-top: 1px solid #e0e0e0; }
      .fk-sim-check__item p { margin: 0 0 0.25rem; }
      .fk-sim-check__spec { font-weight: bold; }
      .fk-sim-check__count { font-size: 0.9em; display: flex; justify-content: space-between; gap: 0.5rem; }
      .fk-sim-check__tag { color: #b71c1c; white-space: nowrap; }
      .fk-sim-check__bad { color: #b71c1c; font-weight: bold; }
      .fk-sim-check__error { color: #b71c1c; font-size: 0.9em; margin: 0 0 0.75rem; }
      .fk-sim-check__actions { display: grid; gap: 0.5rem; }
      .fk-sim-check__actions button { padding: 0.75rem; border-radius: 6px; font-size: 0.95em; cursor: pointer; border: 1px solid #999; background: #fff; color: #222; }
      .fk-sim-check__actions .fk-sim-check__primary { background: #222; color: #fff; border-color: #222; }
      .fk-sim-check__actions button[disabled] { opacity: 0.5; cursor: wait; }
    </style>
    <h2>オーダー商品のオプションをご確認ください</h2>
    ${describeSummary(mismatches)}
    <p class="fk-sim-check__label">対象</p>
    <ul>${mismatches.map(describeOrder).join('')}</ul>
    <p class="fk-sim-check__error" hidden></p>
    <div class="fk-sim-check__actions">
      <button type="button" class="fk-sim-check__primary" data-fk-action="fix">オプションを選んだ内容に合わせて進む</button>
      <button type="button" data-fk-action="back">カートに戻って直す</button>
      <button type="button" data-fk-action="continue" hidden>このまま進む</button>
    </div>`;
  document.body.append(dialog);

  const buttons = [...dialog.querySelectorAll('button')];
  const error = dialog.querySelector('.fk-sim-check__error');
  dialog.addEventListener('close', () => dialog.remove());
  dialog.addEventListener('click', async (e) => {
    const action = e.target instanceof Element ? e.target.closest('[data-fk-action]')?.dataset.fkAction : null;
    if (action === 'back') dialog.close();
    if (action === 'continue') {
      dialog.close();
      onContinue();
    }
    if (action === 'fix') {
      buttons.forEach((b) => (b.disabled = true));
      try {
        await onFix();
      } catch (err) {
        console.error('[fk-sim-option]', err);
        error.textContent = 'うまく合わせられませんでした。カートに戻って直すか、このままお進みください。';
        error.hidden = false;
        buttons.forEach((b) => (b.disabled = false));
        // 普段は出さない。合わせる処理が失敗したときだけ、お客様を止めないために出す
        dialog.querySelector('[data-fk-action="continue"]').hidden = false;
      }
    }
  });
  dialog.showModal();
}

// 配送日時の判定（fk-delivery-date.js）の後、プリント加工費の照合（fk-print-option.js）の前に走る
document.addEventListener(
  'submit',
  (event) => {
    if (!window.FK_SIM_CHECK_ENABLED) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
    const submitter = event.submitter;
    if (submitter && submitter.name !== 'checkout') return;

    // 照合を済ませた（または「このまま進む」を押した）送り直し ⇒ 次の照合（プリント加工費）へ渡す
    if (form.dataset.fkSimChecked === 'true') {
      delete form.dataset.fkSimChecked;
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const proceed = () => {
      form.dataset.fkSimChecked = 'true';
      form.requestSubmit(submitter && submitter.form === form ? submitter : undefined);
    };

    fetchCart()
      .then((cart) => {
        const mismatches = findMismatches(cart.items);
        if (!mismatches.length) return proceed();
        openDialog(mismatches, {
          // 押すたびにカートを読み直し、その時点のずれだけを合わせる（失敗後の押し直しで二重に足さない）
          onFix: async () => {
            const items = (await fetchCart()).items;
            const current = findMismatches(items);
            if (current.length) await fixMismatches(current);
            await goOnKeepingForm(form, items);
          },
          onContinue: proceed,
        });
      })
      // 照合そのものが失敗したら、止めずに進める（最後の網は運営あての新規注文メール）
      .catch((error) => {
        console.error('[fk-sim-option]', error);
        proceed();
      });
  },
  true
);

/*
 * カートの「内容を編集する」（snippets/cart-products.liquid）は本番の sim へ張ってある。
 * 手元の theme dev（127.0.0.1:9292）で見ているときだけ、押した瞬間に手元の sim（ec.localhost:5173）へ向け直し、
 * 戻り先（back）も手元のストアにする（sim は許可したストアの origin 以外の back を捨てる）。
 * ⚠️ Liquid の request.host では見分けられない（theme dev は本物のホスト名で描画する）＝fk-sim-entry.liquid と同じ判定。
 * ⚠️ カートのドロワーは後から差し込まれる ⇒ 押したときに拾う（リンクに個別の script は付けられない）。
 */
const LOCAL = location.hostname === '127.0.0.1' || location.hostname === 'localhost';
if (LOCAL) {
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target instanceof Element ? event.target.closest('a[data-fk-sim-edit]') : null;
      if (!link) return;
      const url = new URL(link.href);
      const local = new URL(url.pathname + url.search, 'http://ec.localhost:5173');
      const back = local.searchParams.get('back');
      if (back) {
        const b = new URL(back);
        local.searchParams.set('back', location.origin + b.pathname);
      }
      link.href = local.toString();
    },
    true
  );
}
